import { forwardRef, useImperativeHandle } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Radio, Row, Select, Spin } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import IntlMessages from '@src/util/IntlMessages';

import DataTypeSelector from '../../components/Shared/DataTypeSelector';
import GroupBy from '../../components/Shared/GroupBy';
import UsersTypeSelector from '../../components/Shared/UsersTypeSelector';
import {
  DASHBOARDS_FORMS,
  DIVISIONS_TYPE,
  EXPRESSION_VALUE_TYPE,
  EXPRESSION_VALUE_TYPE_DEFAULT,
  GROUP_BY_DEFAULT,
  TEMPLATE_IDS_DEFAULT,
  USERS_TYPE,
} from '../../constants';
import useScheduleComplianceForm from './hooks/useScheduleComplianceForm';

const ScheduleComplianceForm = forwardRef(({ chartId, divId, boardId }, formRef) => {
  const intl = useIntl();
  const {
    form,
    loading,
    selectedSchedules,
    setSelectedSchedules,
    setLoadingSchedules,
    disableType,
    fetchedChartData,
    initialScheduleIds,
    orgId,
    divisionIds,
    watchDivisionFilter,
    watchUsersType,
    watchType,
    divisionsOptions,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    handleClear,
    buildBody,
    resetSelectedSchedules,
    onChangeGroupBy,
    resetTemplateIds,
    resetUsersAndGroups,
  } = useScheduleComplianceForm({ chartId, divId, boardId });

  useImperativeHandle(
    formRef,
    () => ({
      ...form,
      handleClear,
      submit: () => form.submit(),
    }),
    [handleClear, form],
  );

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        onFieldsChange={buildBody}
        layout={FORM_LAYOUT_DEFAULT}
        name={DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE}
        initialValues={{
          id: chartId ?? null,
          title: '',
          description: '',
          users: [],
          groups: [],
          schedulesIds: [],
          groupBy: GROUP_BY_DEFAULT,
          type: null,
          valueType: EXPRESSION_VALUE_TYPE_DEFAULT,
          divisionFilter: DIVISIONS_TYPE.ALL,
          usersType: USERS_TYPE.ALL,
          templateIds: TEMPLATE_IDS_DEFAULT,
          divisionIds: [],
          formData: {},
        }}
      >
        <BoxContainer content>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({ id: 'dashboards.chart.form.title' })}
                  />
                }
                value={
                  <Form.Item
                    rules={[
                      { required: true, message: intl.formatMessage({ id: 'form.required' }) },
                    ]}
                    name="title"
                  >
                    <Input />
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    value={intl.formatMessage({ id: 'dashboards.chart.form.description' })}
                  />
                }
                value={
                  <Form.Item name="description">
                    <Input.TextArea showCount rows={4} maxLength={250} />
                  </Form.Item>
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({ id: 'dashboards.chart.form.horizontal.axis' })}
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.scheduleCompliance.groupBy.label',
                    })}
                  />
                }
                value={
                  <Form.Item name="groupBy" className="gx-m-0">
                    <GroupBy onChange={onChangeGroupBy} />
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({ id: 'dashboards.chart.form.vertical.axis' })}
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.result.format.description',
                    })}
                  />
                }
                value={
                  <Form.Item
                    className="gx-m-0"
                    name="valueType"
                    rules={[
                      { required: true, message: intl.formatMessage({ id: 'form.required' }) },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={EXPRESSION_VALUE_TYPE.PERCENTAGE}>
                        {intl.formatMessage({
                          id: 'dashboards.chart.form.counter.result.format.percentage',
                        })}
                      </Radio>
                      <Radio value={EXPRESSION_VALUE_TYPE.NUMBER}>
                        {intl.formatMessage({ id: 'eventTypes.component.number' })}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({ id: 'dashboards.chart.form.counter.filter.by' })}
              />
            </Col>
          </Row>
          <Form.Item noStyle hidden={!!divId}>
            <Row gutter={16}>
              <Col span={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={intl.formatMessage({
                        id: 'dashboards.chart.form.scheduleCompliance.filter.division.label',
                      })}
                    />
                  }
                  value={
                    <>
                      <Form.Item name="divisionFilter" className="gx-m-0">
                        <Radio.Group>
                          <Radio value={DIVISIONS_TYPE.ALL}>
                            <IntlMessages id="divisionSelector.allDivisions" />
                          </Radio>
                          <Radio value={DIVISIONS_TYPE.DIVISIONS}>
                            <IntlMessages id="sidebar.configuration.divisions" />
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                      {watchDivisionFilter === DIVISIONS_TYPE.DIVISIONS && (
                        <KeyValueWidget
                          label={
                            <Title.LabelForm
                              className="gx-mb-2"
                              required
                              value={intl.formatMessage({
                                id: 'dashboards.chart.form.scheduleCompliance.filter.division.select.label',
                              })}
                            />
                          }
                          value={
                            <Form.Item name="divisionIds">
                              <Select
                                onChange={value => {
                                  resetSelectedSchedules();
                                  resetTemplateIds();
                                }}
                                allowClear
                                mode="multiple"
                                options={divisionsOptions}
                              />
                            </Form.Item>
                          }
                        />
                      )}
                    </>
                  }
                />
              </Col>
            </Row>
          </Form.Item>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.users.label',
                    })}
                  />
                }
                value={
                  <UsersTypeSelector
                    usersType={watchUsersType}
                    groupOptions={groupOptions}
                    groupsLoading={groupsLoading}
                    handlePopupScrollGroups={handlePopupScrollGroups}
                    onReset={resetUsersAndGroups}
                    orgId={orgId}
                    userIds={fetchedChartData?.queryParams?.userIds || []}
                  />
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24} className="gx-pb-3">
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    className="gx-pb-2"
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.information.label',
                    })}
                  />
                }
                value={
                  <DataTypeSelector
                    type={watchType}
                    disabledTypes={disableType}
                    selectedSchedules={selectedSchedules}
                    setSelectedSchedules={setSelectedSchedules}
                    setLoadingSchedules={setLoadingSchedules}
                    divisionIds={divId ? [divId] : divisionIds}
                    initialScheduleIds={initialScheduleIds}
                    templateIds={fetchedChartData?.queryParams?.templateIds || []}
                    orgId={orgId}
                    form={form}
                    onTypeChange={resetSelectedSchedules}
                  />
                }
              />
            </Col>
          </Row>
        </BoxContainer>
        <Form.Item name="formData" hidden />
        <Form.Item name="id" hidden />
      </Form>
    </Spin>
  );
});

export default ScheduleComplianceForm;
